import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2 } from 'lucide-react';
import api from '../lib/api';

const PACKS = [
  { code: 'writer_pack', label: 'Writer Pack', price: '£15', credits: 15 },
  { code: 'production_pack', label: 'Production Pack', price: '£39', credits: 40, popular: true },
  { code: 'studio_pack', label: 'Studio Pack', price: '£79', credits: 90 },
];

export default function TopUpModal({ open, onClose, message, pendingAction }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const handleCheckout = useCallback(async (packCode) => {
    setLoading(packCode);
    // Store pending action so it survives the Stripe redirect
    if (pendingAction) {
      sessionStorage.setItem('sp_pending_action', JSON.stringify(pendingAction));
    }
    try {
      const { data } = await api.post('/billing/create-checkout-session', {
        pack_code: packCode,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      console.error(e);
      setLoading(null);
    }
  }, [pendingAction]);

  const goToPricing = () => {
    if (pendingAction) {
      sessionStorage.setItem('sp_pending_action', JSON.stringify(pendingAction));
    }
    onClose();
    navigate('/pricing');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center" style={{ backgroundColor: 'rgba(11,15,26,0.85)' }}>
      <div className="relative w-full max-w-md mx-4 p-8 border border-[#F5EDE4]/[0.06]" style={{ backgroundColor: '#0E1320', fontFamily: "'Manrope', sans-serif" }} data-testid="topup-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#F5EDE4]/30 hover:text-[#F5EDE4]/60 transition-colors" data-testid="topup-close">
          <X className="h-4 w-4" />
        </button>

        <h2 className="text-xl font-medium text-[#F5EDE4] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
          Keep writing your screenplay
        </h2>
        <p className="text-sm text-[#F5EDE4]/40 mb-8">
          {message || "You'll need a few more credits to keep writing your screenplay."}
        </p>

        <div className="space-y-3 mb-6">
          {PACKS.map(pack => (
            <button key={pack.code} onClick={() => handleCheckout(pack.code)} disabled={!!loading}
              className={`w-full flex items-center justify-between p-4 border transition-all duration-300 disabled:opacity-50 ${pack.popular ? 'border-[#D6A85C]/30 hover:border-[#D6A85C]/50' : 'border-[#F5EDE4]/[0.06] hover:border-[#F5EDE4]/[0.12]'}`}
              style={{ backgroundColor: pack.popular ? 'rgba(214,168,92,0.04)' : 'transparent' }}
              data-testid={`topup-${pack.code}`}
            >
              <div className="text-left">
                <span className="text-sm text-[#F5EDE4]/80 font-medium">{pack.label}</span>
                <span className="text-xs text-[#F5EDE4]/30 ml-2">{pack.credits} credits</span>
              </div>
              <div className="flex items-center gap-2">
                {loading === pack.code ? (
                  <Loader2 className="h-4 w-4 animate-spin text-[#D6A85C]" />
                ) : (
                  <span className="text-sm font-medium text-[#D6A85C]">{pack.price}</span>
                )}
              </div>
            </button>
          ))}
        </div>

        <button onClick={goToPricing} className="w-full text-center text-xs text-[#F5EDE4]/25 hover:text-[#F5EDE4]/40 transition-colors py-2" data-testid="topup-see-all">
          See all options including membership
        </button>
      </div>
    </div>
  );
}
